import React, { useEffect, useState } from 'react'
import { ImageBackground, Linking, Platform } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { useB2C } from '@zet/common/hooks/useB2C'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import AddCustomerHeader from '@zet/customer/src/components/addCustomerHeader'
import { colors as zetColors } from '@zet/design-system'
import Avatar from '@zet/design-system/avatar/Avatar'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { H5, SH1, SH3 } from '@zet/design-system/typography'
import { getName } from '@zet/utils/stringConversion'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import { LinearGradient } from 'expo-linear-gradient'
import { StatusBar } from 'expo-status-bar'

import QuestionMarkOutlined from '../../assets/svg/questionMarkOutlined.svg'
import ZETLogo from '../../assets/svg/zetLogo.svg'
import BrandAgentHeader from '../../components/brandAgentHeader'
import ChangePinCode from '../../components/changePincode'
import RecommendtionsHeaderOptionsBS from '../../components/recommendtionsHeaderOptionsBS'
import Reconsent from '../../components/reconsent'
import useVariantListingStore from '../../hooks/useVariantListingStore'

interface IBrandListHeaderV2 {
  categoryName?: string
  customerId: number
  customerName?: string
  customerPhoneNumber?: string | number
  categoryId: number
  isBureauReportExpired?: boolean
  brandFlowPartnerId?: number
  brandSpecificPartnerId?: number | string
  subAgentId?: number
  handleHidePayout?: (hide: boolean) => void
  showPincode?: boolean
}

const isWeb = Platform.OS === 'web'

const BrandListHeaderV2 = ({
  categoryName,
  customerId,
  customerName,
  customerPhoneNumber,
  categoryId,
  isBureauReportExpired,
  subAgentId,
  showPincode = true,
}: IBrandListHeaderV2) => {
  const [
    showRecommandationHeaderOptionsBS,
    setShowRecommandationHeaderOptionsBS,
  ] = useState(false)

  const [showPayout, setPayoutVisibility] = useVariantListingStore((state) => [
    state?.showPayout,
    state?.setPayoutVisibility,
  ])

  const { isBMSFlow, isJfsFlow } = useGetAgentDetailsAndTheme()

  const { navigate, canGoBack, goBack } = useNavigation()

  const { isB2C } = useB2C()

  const eventPrefix = () => {
    if (customerId) return 'brandpage_recommendation'
    else return 'brandpage'
  }

  useEffect(() => {
    if (isWeb && isB2C) {
      publishEvent('screen_view', { screen_name: 'loan_available-screen_view' })
    }
  }, [])

  if (isJfsFlow) {
    return null
  }

  return (
    <>
      {isWeb && isBMSFlow ? (
        <BrandAgentHeader
          customerName={customerName}
          customerPhoneNumber={customerPhoneNumber}
        />
      ) : (
        <>
          {isWeb && isB2C && (
            <ImageBackground
              source={require('../../assets/images/customerHeaderBackground.png')}
            >
              <XStack className="flex items-center justify-end py-4">
                <Stack className="absolute left-[40%]">
                  <ZETLogo />
                </Stack>
                <Pressable
                  className="mr-3 flex-row items-center rounded-full bg-white px-3 py-1"
                  onPress={() => Linking.openURL(`tel:7417274072`)}
                >
                  <QuestionMarkOutlined />
                  <SH3 className="ml-1">Help</SH3>
                </Pressable>
              </XStack>
              <XStack className="w-full rounded-tl-2xl rounded-tr-2xl bg-white px-3 py-3">
                <H5>Top Loan Offers for You</H5>
              </XStack>
            </ImageBackground>
          )}

          {isWeb && !isB2C && <AddCustomerHeader />}

          {!isWeb && (
            <>
              <StatusBar backgroundColor={'#041B3D'} />
              <LinearGradient
                colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
                start={[0, 0]}
                end={[1, 0.0]}
                locations={[0.0, 1.0]}
              >
                <XStack className="items-center justify-between px-3 pb-3 pt-3">
                  <XStack className="items-center space-x-3">
                    <Pressable
                      className="rounded p-1"
                      style={{ backgroundColor: 'rgba(74, 129, 184, 0.14)' }}
                      onPress={() =>
                        canGoBack() ? goBack() : navigate('HomeView')
                      }
                    >
                      <Icon type="ArrowLeft" color="#fff" />
                    </Pressable>
                    {!customerId && !isWeb && categoryName && (
                      <H5 className="text-white">{categoryName}</H5>
                    )}
                  </XStack>

                  <Pressable
                    className="rounded p-1"
                    onPress={() => setShowRecommandationHeaderOptionsBS(true)}
                    {...(!!customerId && {
                      eventName: 'click_action',
                      eventPayload: {
                        item_name: 'more_options_recommendation_page_app',
                      },
                    })}
                  >
                    <Icon type="MoreHorizontal" color="#FFF" />
                  </Pressable>
                </XStack>
                {customerId && (
                  <Stack
                    className="border-t"
                    style={{ borderTopColor: zetColors.tertiary[20] }}
                  >
                    <Stack className="py-3">
                      <XStack className="mx-3 items-center justify-between">
                        <XStack className="items-center">
                          <Avatar name={customerName} size="large" />
                          <Stack>
                            {Boolean(customerName) && (
                              <SH1
                                className="ml-[10] break-words text-white"
                                style={{ width: 200 }}
                              >
                                {getName(customerName + '')}
                              </SH1>
                            )}
                            {Boolean(customerPhoneNumber) && (
                              <SH3 className="ml-[10] break-words text-white">
                                {customerPhoneNumber}
                              </SH3>
                            )}
                          </Stack>
                        </XStack>
                        <XStack className="items-center gap-2">
                          <Pressable
                            className="border-grays-60 rounded border p-1"
                            onPress={() => {
                              Linking.openURL(`tel:${customerPhoneNumber}`)
                            }}
                            eventName="click_action"
                            eventPayload={{
                              item_name: `${eventPrefix()}_call_customer`,
                            }}
                          >
                            <Icon type="PhoneFilled" color="#F5F7FB" />
                          </Pressable>
                          <Pressable
                            className="border-grays-60 rounded border p-1"
                            onPress={() =>
                              Linking.openURL(
                                `https://api.whatsapp.com/send?phone=+91${customerPhoneNumber}`
                              )
                            }
                            eventName="click_action"
                            eventPayload={{
                              item_name: `${eventPrefix()}_message_customer`,
                            }}
                          >
                            <Icon type="Whatsapp" color="#F5F7FB" />
                          </Pressable>
                        </XStack>
                      </XStack>
                    </Stack>
                  </Stack>
                )}
                {!customerId && showPincode && (
                  <ChangePinCode categoryId={categoryId} isGradient />
                )}
              </LinearGradient>
              {/* //! Reconcent flow for app */}
              {!isWeb && isBureauReportExpired && (
                <Reconsent
                  customerName={customerName}
                  customerPhoneNumber={customerPhoneNumber}
                  customerId={customerId}
                  isReconsentFlow={isBureauReportExpired}
                  subAgentId={subAgentId}
                />
              )}
            </>
          )}
        </>
      )}

      <RecommendtionsHeaderOptionsBS
        customerId={customerId}
        isVisible={showRecommandationHeaderOptionsBS}
        onDismiss={() => setShowRecommandationHeaderOptionsBS(false)}
        isPayoutVisible={showPayout}
        handleHidePayout={setPayoutVisibility}
      />
    </>
  )
}

export default React.memo(BrandListHeaderV2)
